(ns app.front.view.complaint.subs
  (:require [app.domain.complaint.model :as complaint-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.municipality :as municipality]
            [app.front.view.complaint.add-form :as complaint-form]
            [app.front.view.complaint.search :as complaint-search]
            [app.front.view.complaint.util :as complaint-util]
            [app.front.view.create-application.gis-search :as gis-search]
            [app.front.view.map-common.util :as map-common-util]
            [app.front.view.workspace.get :as ws-get]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.feature-flags :as ff]
            [core.front.i18n :as i18n]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(def result-layer-id "complaints")

(defn set-selected-group [db selected-group]
  (assoc-in db [::data ::selected-group] selected-group))

(defn- sort-groups-by-municipality-name [lang groups]
  (sort-by #(municipality-model/municipality-name % lang) groups))

(defn- get-workspace-configs-fx [selected-group]
  (mapv (fn [ag]
          [:dispatch [::ws-get/get-workspace-config (:group/id ag)]])
        (:authority-groups selected-group)))

(rf/reg-event-fx ::init
  (fn [{:keys [db]} _]
    {:db           (-> (assoc-in db [::data ::loading?] (empty? (get-in db [::data ::groups])))
                       (assoc ::map-visible? true))
     :comm/command {:command          :complaint/groups-for-complaint-management
                    :method           :get
                    :on-success       [::init.success]
                    :finally-dispatch [::init.done]}}))

(rf/reg-event-fx ::init.success
  (fn [{:keys [db]} [_ status groups]]
    (let [groups           (sort-groups-by-municipality-name (i18n/language-query db) groups)
          current-group    (get-in db [::data ::selected-group])
          pre-selected-gid (get-in db [::data ::pre-selected-group-id])
          selected-group   (or (some #{current-group} groups)
                               (when pre-selected-gid
                                 (p/find-first
                                   groups
                                   (fn [{:keys [authority-groups]}]
                                     (some? (p/find-first
                                              authority-groups
                                              {:authority-group-id pre-selected-gid})))))
                               (first groups))]
      (merge {:db (-> (assoc-in db [::data ::groups] groups)
                      (assoc-in [::data ::groups-status] status)
                      (assoc-in [::data ::selected-group] selected-group))}
             (when selected-group
               {:fx (into [[:dispatch [::entities/merge-entity-db status (->> selected-group
                                                                              :authority-groups
                                                                              (mapcat (fn [ag]
                                                                                        (concat (:categories ag)
                                                                                                (:complaint-sources ag))))
                                                                              du/entities->entity-db)]]
                           [:dispatch [::municipality/load-map-config (:group/id selected-group)]]
                           [:dispatch [::complaint-search/init (->> selected-group
                                                                    :authority-groups
                                                                    (map :authority-group-id))]]]
                          (get-workspace-configs-fx selected-group))})))))

(rf/reg-event-db ::init.done
  (fn [db _]
    (assoc-in db [::data ::loading?] false)))

(defn purge-data-except-groups [db]
  (update db ::data select-keys [::groups ::groups-status ::selected-group]))

(rf/reg-event-fx ::leave
  (fn [{:keys [db]} _]
    {:db (purge-data-except-groups db)
     :fx [[:dispatch [::complaint-search/clear-search-results]]
          [:dispatch [::gis-search/set-search-value ""]]
          [:dispatch [::complaint-form/clear-data]]]}))

(rf/reg-event-fx ::find-property-complaints.success
  (fn [{:keys [db]} [_ property-id _ resp]]
    (if (ff/enabled? :feature-flag.id/streamline-ce-admin-process)
      {:db (assoc-in db [::data ::property-complaints property-id] resp)}
      (merge {:db (assoc-in db [::data ::property-complaints property-id] resp)}
             (when (empty? resp)
               {:dispatch [::complaint-form/start-create-complaint]})))))


(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::loading?
  :<- [::data]
  :-> ::loading?)

(rf/reg-sub ::modal-loading?
  :<- [::data]
  :-> ::modal-loading?)

(rf/reg-sub ::modal-saving?
  :<- [::data]
  :-> ::modal-saving?)

(rf/reg-sub ::code-enforcement-groups
  :<- [::data]
  :<- [:language]
  (fn [[data lang] _]
    (->> data
         ::groups
         (sort-groups-by-municipality-name lang))))

(rf/reg-event-db ::set-pre-selected-group-id
  (fn [db [_ group-id]]
    (assoc-in db [::data ::pre-selected-group-id] group-id)))

(rf/reg-event-fx ::select-group
  (fn [{:keys [db]} [_ group]]
    {:db (-> (assoc-in db [::data ::selected-group] group)
             purge-data-except-groups)
     :fx (into [[:dispatch [::municipality/load-map-config (:group/id group)]]
                [:dispatch [::complaint-search/init (->> group :authority-groups (map :authority-group-id))]]]
               (get-workspace-configs-fx group))}))

(rf/reg-sub ::selected-group
  :<- [::data]
  :-> ::selected-group)

(rf/reg-sub ::permit-type-for-case-creation
  :<- [::data]
  :-> ::permit-type-for-case-creation)

(rf/reg-event-db ::set-permit-type-for-case-creation
  (fn [db [_ permit-type]]
    (assoc-in db [::data ::permit-type-for-case-creation] permit-type)))

(rf/reg-sub ::group-for-case-creation
  :<- [::selected-group]
  :<- [::permit-type-for-case-creation]
  (fn [[group permit-type] _]
    (-> group
        (dissoc :authority-groups)
        (merge (->> group
                    :authority-groups
                    (some #(when (= permit-type (:permit-type %)) %)))))))

(rf/reg-sub ::available-categories
  :<- [::selected-group]
  :<- [::permit-type-for-case-creation]
  :<- [:language]
  (fn [[group permit-type language] _]
    (->> group
         :authority-groups
         (filter #(= permit-type (:permit-type %)))
         (mapcat :categories)
         (filter (partial config-model/allow-workspace-creation? true))
         (config-model/sort-group-configs language))))

(rf/reg-sub ::map-visible?
  :-> ::map-visible?)

(rf/reg-event-db ::toggle-map-visibility
  (fn [db _]
    (update db ::map-visible? not)))


(rf/reg-sub ::property-tr
  :<- [::selected-group]
  :<- [:tr]
  (fn [[config tr] _]
    (property-model/property-tr-fn tr config)))

(rf/reg-sub ::complaint-tr
  :<- [::property-tr]
  (fn [tr [_ permit-type]]
    (-> permit-type
        i18n-model/permit-type-specific-keys
        (i18n-model/wrap-tr-fn tr))))

(defn set-selected-property-and-point [db gis-feature point]
  (-> (assoc-in db [::data ::selected-property] gis-feature)
      (assoc-in [::data ::new-complaint-coordinates] point)
      (p/dissoc-in [::data ::complaint-in-view])
      (p/dissoc-in [::data ::complaints-to-case])))

(defn find-complaints-cmd [db feature]
  (when (:geometry feature)
    (complaint-search/find-complaints-cmd (->> db ::data ::selected-group :authority-groups (map :authority-group-id))
                                          (:geometry feature)
                                          ;; Honor the state filter here too
                                          (->> (complaint-search/format-filter-conditions db)
                                               (filter #(-> % :filter.condition/attribute (= :domain/state))))
                                          [::find-property-complaints.success (-> feature :property :property/id)])))

(defn apply-close-edit [db]
  (update db ::data dissoc ::complaint-data-edit ::close-edit-after-save?))

(defn apply-close-modal [db]
  (-> db
      (dissoc ::selected-complaint)
      (p/dissoc-in [::data ::close-modal-after-save?])))

(defn update-complaint-fx [db complaint-id updates {:keys [refresh-event on-success-event]}]
  (let [refresh-event (or refresh-event [::complaint-search/search])]
    {:comm/command {:command          :complaint/update
                    :data             (merge {:complaint/id complaint-id} updates)
                    :on-success       [::update-complaint.success
                                       refresh-event
                                       on-success-event]
                    :finally-dispatch [::load-modal.done]}
     :db           (-> db
                       (p/dissoc-in [::data ::delayed-save-active])
                       (assoc-in [::data ::save-active] true))}))

(defn mutation-options [{:keys [refresh-event cleanup-event]}]
  (p/assoc-if {}
              :refresh-event refresh-event
              :on-success-event cleanup-event))

(rf/reg-event-fx ::update-complaint
  (fn [{:keys [db]} [_ complaint-id updates options]]
    (update-complaint-fx db complaint-id updates options)))

(rf/reg-event-fx ::update-complaint.success
  (fn [{:keys [db]} [_ refresh-event on-success-event status changes]]
    (merge {:db (cond-> (update db ::data dissoc ::save-active ::modal-saving?)
                  (get-in db [::data ::close-edit-after-save?]) apply-close-edit
                  (get-in db [::data ::close-modal-after-save?]) apply-close-modal)
            :fx (cond-> [[:dispatch [::entities/apply-changes status changes]]
                         [:dispatch refresh-event]]
                  on-success-event (conj [:dispatch on-success-event]))}
           (when-let [feature (-> db ::data ::selected-property)]
             (find-complaints-cmd db feature)))))

(rf/reg-event-fx ::show-complaint-modal
  (fn [{:keys [db]} [_ id]]
    {:db           (-> db
                       (assoc ::selected-complaint id)
                       (assoc-in [::data ::modal-loading?] true))
     :comm/command {:command          :complaint/get
                    :data             {:complaint-id id}
                    :method           :get
                    :on-success       [::entities/merge-entity-db]
                    :finally-dispatch [::load-modal.done]}}))


(rf/reg-event-db ::load-modal.done
  (fn [db _]
    (update db ::data dissoc ::modal-loading? ::modal-saving?)))


(defn remove-invalid-fields [complaint-data]
  (reduce (fn [data field]
            (let [value (field data)]
              (if (or (nil? value)
                      (complaint-util/valid? field value))
                data
                (dissoc data field))))
          complaint-data complaint-model/complaint-edit-keys))


(defn set-existing-complaint-data
  ([db k v debounce-timeout]
   (set-existing-complaint-data db k v debounce-timeout nil))
  ([db k v debounce-timeout options]
   (let [new-v (when-not (and (string? v) (str/blank? v)) v)]
     (if (or (get-in db [::data ::delayed-save-active])
             (not= new-v (get-in db [::data ::complaint-data-edit k])))
       (let [new-db (-> db
                        (assoc-in [::data ::complaint-data-edit k] new-v)
                        (assoc-in [::data ::delayed-save-active] true))]
         {:dispatch-debounce [{:id      "complaint-save-debounce"
                               :timeout debounce-timeout
                               :action  :dispatch
                               :event   [::update-complaint
                                         (::selected-complaint new-db)
                                         (remove-invalid-fields
                                           (get-in new-db [::data ::complaint-data-edit]))
                                         (merge {:refresh-event [::complaint-search/search]} options)]}]
          :db                new-db})
       {:db db}))))

(rf/reg-event-fx ::set-existing-complaint-data-debounce
  (fn [{:keys [db]} [_ k v options]]
    (set-existing-complaint-data db k v 1000 options)))

(rf/reg-event-fx ::set-existing-complaint-data
  (fn [{:keys [db]} [_ k v options]]
    (set-existing-complaint-data db k v 0 options)))

(rf/reg-sub ::complaint-data-edit
  :<- [::data]
  :-> ::complaint-data-edit)

(rf/reg-sub ::complaint-reopen
  :<- [::data]
  :-> ::complaint-reopen)

(rf/reg-event-fx ::set-complaint-reopen
  (fn [{:keys [db]} [_ reopen?]]
    {:db (assoc-in db [::data ::complaint-reopen] reopen?)}))

(rf/reg-event-db ::start-edit
  (fn [db [_ complaint-data]]
    (-> db (assoc-in [::data ::complaint-data-edit] complaint-data))))

(rf/reg-event-fx ::close-edit
  (fn [{:keys [db]} _]
    (merge
      {:dispatch [::complaint-form/clear-data]}
      (if (or (get-in db [::data ::delayed-save-active])
              (get-in db [::data ::save-active]))
        {:dispatch-debounce [{:id     "complaint-save-debounce"
                              :action :flush}]
         :db                (-> db
                                (assoc-in [::data ::close-edit-after-save?] true)
                                (assoc-in [::data ::modal-saving?] true))}
        {:db (apply-close-edit db)}))))


(rf/reg-event-fx ::hide-complaint-modal
  (fn [{:keys [db]} _]
    (let [edit  (boolean (get-in db [::data ::complaint-data-edit]))
          wait? (or (get-in db [::data ::delayed-save-active])
                    (get-in db [::data ::save-active]))]
      (merge
        {:dispatch [::complaint-form/clear-data]}
        (cond
          (and edit wait?)
          {:db                (-> db
                                  (assoc-in [::data ::modal-saving?] true)
                                  (assoc-in [::data ::close-edit-after-save?] true)
                                  (assoc-in [::data ::close-modal-after-save?] true))
           :dispatch-debounce [{:id     "complaint-save-debounce"
                                :action :flush}]}
          edit
          {:db (-> db
                   apply-close-edit
                   apply-close-modal)}
          :else {:db (apply-close-modal db)})))))

(rf/reg-sub ::selected-complaint :-> ::selected-complaint)


(defn single-feature-selected [db ^js feature]
  (p/assoc-if {:dispatch [::open-complaint-view (.getId feature)]}
              :db (when (-> db ::data ::selected-property :property :property/id
                            (not= (.get feature "propertyId")))
                    ;; Remove property selection if this pin is on a different property
                    (p/dissoc-in db [::data ::selected-property]))))

(rf/reg-event-db ::set-layer-id-order
  (fn [db [_ layer-ids]]
    (assoc db ::layer-id-order layer-ids)))

(rf/reg-sub ::layer-id-order
  :-> ::layer-id-order)

(rf/reg-event-fx ::map-click
  (map-common-util/click-handler-fn {:single-feature-fx    single-feature-selected
                                     :search-success-evt   ::map-click.success
                                     :search-not-found-evt ::map-click.not-found
                                     :do-point-search?     true
                                     :cluster-layer-id     result-layer-id
                                     :layer-id-fn          ::layer-id-order}))


(rf/reg-event-fx ::map-click.success
  (fn [{:keys [db]} [_ click-coordinates _ feature]]
    (merge {:db       (set-selected-property-and-point db feature click-coordinates)
            :dispatch [::gis-search/set-search-value (or (-> feature :address :address/full-address)
                                                         "")]}
           (find-complaints-cmd db feature))))


(rf/reg-event-fx ::map-click.not-found
  (fn [{:keys [db]} [_ coordinates _status _response]]
    {:db       (-> (p/dissoc-in db [::data ::selected-property])
                   (assoc-in [::data ::new-complaint-coordinates] coordinates))
     :dispatch [::gis-search/set-search-value ""]}))


(rf/reg-event-fx ::submit-new-complaint.success
  (fn [{:keys [db]} [_ complaint]]
    {:db       (update-in db [::data ::property-complaints (:property/id complaint)] conj complaint)
     :dispatch [::complaint-search/add-new-search-result complaint]}))


(rf/reg-event-fx ::open-complaint-view
  (fn [{:keys [db]} [_ complaint-id]]
    {:db           (assoc-in db [::data ::complaint-in-view] {:complaint/id complaint-id
                                                              :loading?     true})
     :dispatch     [::complaint-form/clear-data]
     :comm/command {:command          :complaint/get
                    :data             {:complaint-id complaint-id}
                    :method           :get
                    :on-success       [::entities/merge-entity-db]
                    :finally-dispatch [::complaint-get.done]}}))


(rf/reg-event-db ::close-complaint-view
  (fn [db _]
    (p/dissoc-in db [::data ::complaint-in-view])))


(rf/reg-event-db ::complaint-get.done
  (fn [db _]
    (assoc-in db [::data ::complaint-in-view :loading?] false)))

(rf/reg-event-db ::use-location
  (fn [db [_ location]]
    (assoc-in db [::data ::location] location)))

(rf/reg-event-db ::clear-location
  (fn [db _]
    (-> (dissoc db
                :app.front.view.create-application.create-v2/manual-address
                :app.front.view.create-application.create-v2/manual-input)
        (p/dissoc-in [::data ::location]))))


(rf/reg-sub ::complaint-in-view
  :<- [::data]
  :-> ::complaint-in-view)


(rf/reg-sub ::new-complaint-coordinates
  :<- [::data]
  :-> ::new-complaint-coordinates)


(rf/reg-sub ::create-in-progress?
  :<- [::data]
  :-> ::create-in-progress?)


(rf/reg-sub ::complaint-view-loading?
  :<- [::data]
  #(-> % ::complaint-in-view :loading?))


(rf/reg-sub ::geojson-results
  :<- [::complaint-search/search-results]
  :<- [::complaint-in-view]
  (fn [[results complaint-in-view] _]
    (->> results
         (map (fn [complaint]
                {:properties {:propertyId (:property/id complaint)
                              :selected   (= (:complaint/id complaint) (:complaint/id complaint-in-view))
                              :kind       "complaint"}
                 :type       "Feature"
                 :geometry   (:location complaint)
                 :id         (:complaint/id complaint)}))
         property-model/geojson-feature-collection)))


(rf/reg-sub ::property-complaints
  :<- [::data]
  (fn [data [_ property-id]]
    (get-in data [::property-complaints property-id])))






(rf/reg-event-fx ::select-property
  (fn [{:keys [db]} [_ {:keys [geometry-point] :as feature}]]
    (merge {:db (-> (set-selected-property-and-point db feature geometry-point)
                    (assoc-in [::data ::map-center] geometry-point))}
           (find-complaints-cmd db feature))))


(rf/reg-sub ::selected-property
  :<- [::data]
  :-> ::selected-property)


(rf/reg-sub ::location
  :<- [::data]
  :-> ::location)


(rf/reg-sub ::map-center
  :<- [::data]
  :-> ::map-center)

(rf/reg-event-fx ::toggle-adding-complaints-to-case
  (fn [{:keys [db]} [_ selecting?]]
    (merge {:db (assoc-in db [::data ::adding-complaints-to-case?] selecting?)}
           (when-not selecting?
             {:dispatch [::clear-added-complaints]}))))

(rf/reg-event-db ::update-new-case-complaints
  (fn [db [_ selecting? complaint]]
    (let [op                (fnil (if selecting? conj disj) #{})
          accepted-group-id (-> db ::data ::complaints-to-case first :group/id)]
      (cond-> db
        (or (nil? accepted-group-id) (= accepted-group-id (:group/id complaint)))
        (update-in [::data ::complaints-to-case] op complaint)))))

(rf/reg-event-db ::clear-added-complaints
  (fn [db _]
    (p/dissoc-in db [::data ::complaints-to-case])))

(rf/reg-sub ::adding-complaints-to-case?
  :<- [::data]
  :-> ::adding-complaints-to-case?)

(rf/reg-sub ::complaints-to-case
  :<- [::data]
  :-> ::complaints-to-case)

(rf/reg-sub ::complaint-photo-ids
  (fn [[_ complaint-id]]
    (rf/subscribe [::entities/entities
                   {:entity/type   :entity.type/file
                    :file/category :file.category/complaint-photo
                    :entity/parent complaint-id}]))
  (fn [photo-entites]
    (map :db/id photo-entites)))


(rf/reg-sub ::write-auth-permit-types
  :<- [:session/write-auth-authority-groups]
  :<- [::selected-group]
  (fn [[write-auth-groups selected-group]]
    (let [write-auth-group-ids (du/entity-id-set write-auth-groups)]
      (->> selected-group
           :authority-groups
           (filter #(write-auth-group-ids (:authority-group-id %)))
           (map :permit-type)
           set))))

(rf/reg-sub ::write-auth-permit-type?
  :<- [::write-auth-permit-types]
  (fn [writable-permit-types [_ permit-type]]
    (contains? writable-permit-types permit-type)))
